import React, { useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const FIELD = "mt-1 w-full rounded-md border border-[#9fb1b9] bg-white px-3 py-2.5 text-[16px] text-[#0f2530] focus:outline focus:outline-3 focus:outline-[#007a9e]/40";
const LABEL = "block text-[14px] font-semibold text-[#0f2530]";
const ERR = "mt-1 text-[13px] text-[#b42318]";
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const LI_RE = /^https?:\/\/([a-z0-9-]+\.)*linkedin\.com(\/.*)?$/i;
const OK_EXT = [".pdf", ".doc", ".docx"];

const validate = (v, file) => {
  const e = {};
  if (!v.full_name.trim()) e.full_name = "Full name is required";
  if (!EMAIL_RE.test(v.email.trim())) e.email = "Valid email is required";
  if (v.phone.replace(/\D/g, "").length < 7) e.phone = "Phone is required";
  if (!v.states.length) e.states = "Choose at least one state";
  if (v.states.includes("OTHER") && !v.other_states.trim()) e.other_states = "Tell us which other state(s)";
  if (!v.role.trim()) e.role = "Current or last role is required";
  if (v.linkedin_url.trim() && !LI_RE.test(v.linkedin_url.trim())) e.linkedin_url = "Use a linkedin.com URL starting with http(s)://";
  if (!v.network_note.trim()) e.network_note = "Tell us about your network";
  else if (v.network_note.length > 1000) e.network_note = "Keep it under 1,000 characters";
  if (file) {
    if (!OK_EXT.some((x) => file.name.toLowerCase().endsWith(x))) e.resume = "Resume must be a PDF, DOC or DOCX file";
    else if (file.size > 5 * 1024 * 1024) e.resume = "Resume must be 5 MB or smaller";
  }
  return e;
};

const Field = ({ id, label, err, children }) => (
  <div className="mt-4">
    <label htmlFor={id} className={LABEL}>{label}</label>
    {children}
    {err && <div id={`${id}-err`} className={ERR}>{err}</div>}
  </div>
);

// Right-side application drawer (full-screen on phones). Esc closes; focus returns to the opener.
export const CareersApplyDrawer = ({ open, states, initialState, onClose }) => {
  const empty = { full_name: "", email: "", phone: "", states: [], other_states: "", role: "", linkedin_url: "", network_note: "", company_website: "" };
  const [v, setV] = useState(empty);
  const [file, setFile] = useState(null);
  const [errs, setErrs] = useState({});
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const panel = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setV({ ...empty, states: initialState ? [initialState] : [] }); setFile(null); setErrs({}); setSummary(""); setDone(false);
    setTimeout(() => panel.current?.querySelector("input,button")?.focus(), 50);
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && panel.current) {
        const f = [...panel.current.querySelectorAll("a,button,input,textarea,select")].filter((x) => !x.disabled && x.offsetParent);
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, initialState]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));
  const toggle = (code) => setV((x) => ({ ...x, states: x.states.includes(code) ? x.states.filter((s) => s !== code) : [...x.states, code] }));
  const aria = (k) => ({ "aria-invalid": !!errs[k], "aria-describedby": errs[k] ? `ca-${k}-err` : undefined });

  const submit = async (e) => {
    e.preventDefault();
    const ve = validate(v, file);
    setErrs(ve);
    if (Object.keys(ve).length) { setSummary(`Please fix ${Object.keys(ve).length} field${Object.keys(ve).length === 1 ? "" : "s"} below.`); return; }
    setBusy(true); setSummary("");
    const fd = new FormData();
    Object.entries(v).forEach(([k, val]) => fd.append(k, Array.isArray(val) ? val.join(",") : val));
    fd.append("source_state", initialState || "");
    if (file) fd.append("resume", file);
    try {
      const r = await fetch(`${API}/api/careers/apply`, { method: "POST", body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const se = d.detail?.errors || {};
        setErrs(se);
        setSummary(d.detail?.message || (typeof d.detail === "string" ? d.detail : "Something went wrong. Please try again."));
        return;
      }
      setDone(true);
    } catch { setSummary("Something went wrong. Please try again."); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60]" data-testid="careers-apply-drawer">
      <div className="absolute inset-0 bg-[#0f2530]/50" onClick={onClose} aria-hidden />
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby="ca-title"
        className="absolute inset-y-0 right-0 w-full overflow-y-auto bg-white p-5 shadow-2xl sm:max-w-[520px] sm:p-7">
        <div className="flex items-start justify-between gap-3">
          <h2 id="ca-title" className="text-[22px] font-bold text-[#0f2530]">Apply: Founding State Partner</h2>
          <button type="button" onClick={onClose} className="min-h-[44px] min-w-[44px] rounded-md text-[22px] text-[#4b5f68] hover:bg-[#f3f6f7]" aria-label="Close" data-testid="careers-apply-close">×</button>
        </div>
        {done ? (
          <div className="mt-10 text-center" data-testid="careers-apply-success" role="status">
            <div className="text-[44px] text-[#15803d]" aria-hidden>✓</div>
            <h3 className="mt-2 text-[20px] font-bold text-[#0f2530]">Thanks, your application is in</h3>
            <p className="mt-2 text-[16px] text-[#374151]">Dirk will review it and reply by email.</p>
            <button type="button" onClick={onClose} className="mt-6 min-h-[44px] rounded-md bg-[#007a9e] px-5 font-semibold text-white hover:bg-[#005f7b]" data-testid="careers-apply-back">Back to Careers</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate data-testid="careers-apply-form">
            {summary && <div role="alert" className="mt-4 rounded-md border border-[#fca5a5] bg-[#fef2f2] px-3 py-2 text-[14px] text-[#7f1d1d]" data-testid="careers-apply-error-summary">{summary}</div>}
            <Field id="ca-full_name" label="Full name *" err={errs.full_name}><input id="ca-full_name" className={FIELD} value={v.full_name} onChange={set("full_name")} autoComplete="name" {...aria("full_name")} data-testid="careers-field-name" /></Field>
            <Field id="ca-email" label="Email *" err={errs.email}><input id="ca-email" type="email" className={FIELD} value={v.email} onChange={set("email")} autoComplete="email" {...aria("email")} data-testid="careers-field-email" /></Field>
            <Field id="ca-phone" label="Phone *" err={errs.phone}><input id="ca-phone" type="tel" className={FIELD} value={v.phone} onChange={set("phone")} autoComplete="tel" {...aria("phone")} data-testid="careers-field-phone" /></Field>
            <fieldset className="mt-4" aria-describedby={errs.states ? "ca-states-err" : undefined}>
              <legend className={LABEL}>State(s) *</legend>
              <div className="mt-1 grid grid-cols-2 gap-1">
                {[...states.filter((s) => s.status === "open"), { code: "OTHER", name: "Other" }].map((s) => (
                  <label key={s.code} className="flex min-h-[40px] items-center gap-2 text-[15px] text-[#0f2530]">
                    <input type="checkbox" className="h-4 w-4 accent-[#007a9e]" checked={v.states.includes(s.code)} onChange={() => toggle(s.code)} data-testid={`careers-state-check-${s.code}`} /> {s.name}
                  </label>
                ))}
              </div>
              {errs.states && <div id="ca-states-err" className={ERR}>{errs.states}</div>}
            </fieldset>
            {v.states.includes("OTHER") && (
              <Field id="ca-other_states" label="Which other state(s)? *" err={errs.other_states}><input id="ca-other_states" className={FIELD} value={v.other_states} onChange={set("other_states")} {...aria("other_states")} data-testid="careers-field-other-states" /></Field>
            )}
            <Field id="ca-role" label="Current or last role *" err={errs.role}><input id="ca-role" className={FIELD} value={v.role} onChange={set("role")} {...aria("role")} data-testid="careers-field-role" /></Field>
            <Field id="ca-linkedin_url" label="LinkedIn URL (optional)" err={errs.linkedin_url}><input id="ca-linkedin_url" type="url" className={FIELD} value={v.linkedin_url} onChange={set("linkedin_url")} placeholder="https://www.linkedin.com/in/…" {...aria("linkedin_url")} data-testid="careers-field-linkedin" /></Field>
            <Field id="ca-network_note" label="Your network in a few sentences *" err={errs.network_note}>
              <textarea id="ca-network_note" rows={5} maxLength={1000} className={FIELD} value={v.network_note} onChange={set("network_note")} {...aria("network_note")} data-testid="careers-field-network" />
              <div className="mt-1 text-right text-[12px] text-[#4b5f68]" aria-live="polite">{v.network_note.length} / 1,000</div>
            </Field>
            <Field id="ca-resume" label="Resume (optional; PDF, DOC or DOCX, max 5 MB)" err={errs.resume}>
              <input id="ca-resume" type="file" accept=".pdf,.doc,.docx" className="mt-1 block w-full text-[14px]" onChange={(e) => setFile(e.target.files?.[0] || null)} {...aria("resume")} data-testid="careers-field-resume" />
            </Field>
            <div aria-hidden className="absolute left-[-10000px] top-auto h-px w-px overflow-hidden">
              <label htmlFor="ca-company_website">Company website</label>
              <input id="ca-company_website" tabIndex={-1} autoComplete="off" value={v.company_website} onChange={set("company_website")} />
            </div>
            <button type="submit" disabled={busy} className="mt-6 min-h-[48px] w-full rounded-md bg-[#007a9e] font-bold text-white hover:bg-[#005f7b] disabled:opacity-70" data-testid="careers-apply-submit">{busy ? "Sending…" : "Submit application"}</button>
            <p className="mt-3 text-[13px] text-[#4b5f68]">We use your details only to review your application and contact you about this role. See our <a href="/privacy" className="text-[#005f7b] underline">Privacy Policy</a>.</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default CareersApplyDrawer;
